"use strict";
/**
 * User validation rules and utilities
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.USERNAME_REQUIREMENTS = exports.PASSWORD_REQUIREMENTS = void 0;
exports.validateEmail = validateEmail;
exports.validatePassword = validatePassword;
exports.validateUsername = validateUsername;
exports.validateUserPreferences = validateUserPreferences;
exports.validateUserRegistration = validateUserRegistration;
exports.validateUserProfileUpdate = validateUserProfileUpdate;
const languages_1 = require("../constants/languages");
/**
 * Password strength requirements
 */
exports.PASSWORD_REQUIREMENTS = {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBERS: true,
    REQUIRE_SPECIAL_CHARS: false,
};
/**
 * Email validation regex (basic)
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Username requirements
 */
exports.USERNAME_REQUIREMENTS = {
    MIN_LENGTH: 3,
    MAX_LENGTH: 30,
    ALLOWED_CHARS: /^[a-zA-Z0-9_-]+$/,
    RESERVED_NAMES: [
        "admin",
        "root",
        "user",
        "test",
        "api",
        "www",
        "mail",
        "ftp",
        "localhost",
        "lingoshade",
        "support",
        "help",
        "info",
        "contact",
    ],
};
/**
 * Validate email address
 */
function validateEmail(email) {
    const errors = [];
    if (!email) {
        errors.push("Email is required");
    }
    else if (!EMAIL_REGEX.test(email)) {
        errors.push("Invalid email format");
    }
    else if (email.length > 254) {
        errors.push("Email is too long");
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings: [],
    };
}
/**
 * Validate password strength
 */
function validatePassword(password) {
    const errors = [];
    const warnings = [];
    if (!password) {
        errors.push("Password is required");
        return { isValid: false, errors, warnings };
    }
    if (password.length < exports.PASSWORD_REQUIREMENTS.MIN_LENGTH) {
        errors.push(`Password must be at least ${exports.PASSWORD_REQUIREMENTS.MIN_LENGTH} characters long`);
    }
    if (password.length > exports.PASSWORD_REQUIREMENTS.MAX_LENGTH) {
        errors.push(`Password must be no more than ${exports.PASSWORD_REQUIREMENTS.MAX_LENGTH} characters long`);
    }
    if (exports.PASSWORD_REQUIREMENTS.REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
        errors.push("Password must contain at least one uppercase letter");
    }
    if (exports.PASSWORD_REQUIREMENTS.REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
        errors.push("Password must contain at least one lowercase letter");
    }
    if (exports.PASSWORD_REQUIREMENTS.REQUIRE_NUMBERS && !/\d/.test(password)) {
        errors.push("Password must contain at least one number");
    }
    if (exports.PASSWORD_REQUIREMENTS.REQUIRE_SPECIAL_CHARS &&
        !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push("Password must contain at least one special character");
    }
    // Common password warnings
    if (password.toLowerCase().includes("password")) {
        warnings.push('Password contains the word "password"');
    }
    if (password.toLowerCase().includes("123")) {
        warnings.push("Password contains sequential numbers");
    }
    if (/(.)\1{2,}/.test(password)) {
        warnings.push("Password contains repeated characters");
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}
/**
 * Validate username
 */
function validateUsername(username) {
    const errors = [];
    const warnings = [];
    if (!username) {
        errors.push("Username is required");
        return { isValid: false, errors, warnings };
    }
    if (username.length < exports.USERNAME_REQUIREMENTS.MIN_LENGTH) {
        errors.push(`Username must be at least ${exports.USERNAME_REQUIREMENTS.MIN_LENGTH} characters long`);
    }
    if (username.length > exports.USERNAME_REQUIREMENTS.MAX_LENGTH) {
        errors.push(`Username must be no more than ${exports.USERNAME_REQUIREMENTS.MAX_LENGTH} characters long`);
    }
    if (!exports.USERNAME_REQUIREMENTS.ALLOWED_CHARS.test(username)) {
        errors.push("Username can only contain letters, numbers, underscores, and hyphens");
    }
    if (exports.USERNAME_REQUIREMENTS.RESERVED_NAMES.includes(username.toLowerCase())) {
        errors.push("This username is reserved");
    }
    if (/^\d+$/.test(username)) {
        warnings.push("Username should not be only numbers");
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}
/**
 * Validate user preferences
 */
function validateUserPreferences(nativeLanguage, learningLanguages, proficiencyLevel) {
    const errors = [];
    const warnings = [];
    // Native language validation
    if (nativeLanguage && !isValidLanguageCode(nativeLanguage)) {
        errors.push("Invalid native language code");
    }
    // Learning languages validation
    if (learningLanguages) {
        if (learningLanguages.length === 0) {
            warnings.push("No learning languages selected");
        }
        for (const lang of learningLanguages) {
            if (!isValidLanguageCode(lang)) {
                errors.push(`Invalid learning language code: ${lang}`);
            }
        }
        if (nativeLanguage && learningLanguages.includes(nativeLanguage)) {
            warnings.push("Native language is also set as a learning language");
        }
    }
    // Proficiency level validation
    if (proficiencyLevel && !isValidLevelCode(proficiencyLevel)) {
        errors.push("Invalid proficiency level");
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}
/**
 * Helper function to check if language code is valid
 * Uses SupportedLanguages from constants to avoid duplication
 */
function isValidLanguageCode(code) {
    return languages_1.SupportedLanguages.includes(code);
}
/**
 * Helper function to check if level code is valid
 */
function isValidLevelCode(code) {
    const validLevels = [
        "BEGINNER",
        "ELEMENTARY",
        "INTERMEDIATE",
        "UPPER_INTERMEDIATE",
        "ADVANCED",
        "PROFICIENT",
    ];
    return validLevels.includes(code);
}
/**
 * Comprehensive user registration validation
 */
function validateUserRegistration(userData) {
    const allErrors = [];
    const allWarnings = [];
    // Validate email
    const emailValidation = validateEmail(userData.email);
    allErrors.push(...emailValidation.errors);
    allWarnings.push(...emailValidation.warnings);
    // Validate username
    const usernameValidation = validateUsername(userData.username);
    allErrors.push(...usernameValidation.errors);
    allWarnings.push(...usernameValidation.warnings);
    // Validate password
    const passwordValidation = validatePassword(userData.password);
    allErrors.push(...passwordValidation.errors);
    allWarnings.push(...passwordValidation.warnings);
    // Validate preferences
    const preferencesValidation = validateUserPreferences(userData.nativeLanguage, userData.learningLanguages, userData.proficiencyLevel);
    allErrors.push(...preferencesValidation.errors);
    allWarnings.push(...preferencesValidation.warnings);
    return {
        isValid: allErrors.length === 0,
        errors: allErrors,
        warnings: allWarnings,
    };
}
/**
 * Validate user profile update
 */
function validateUserProfileUpdate(updateData) {
    const allErrors = [];
    const allWarnings = [];
    // Validate email if provided
    if (updateData.email !== undefined) {
        const emailValidation = validateEmail(updateData.email);
        allErrors.push(...emailValidation.errors);
        allWarnings.push(...emailValidation.warnings);
    }
    // Validate username if provided
    if (updateData.username !== undefined) {
        const usernameValidation = validateUsername(updateData.username);
        allErrors.push(...usernameValidation.errors);
        allWarnings.push(...usernameValidation.warnings);
    }
    // Validate preferences if provided
    if (updateData.nativeLanguage !== undefined ||
        updateData.learningLanguages !== undefined ||
        updateData.proficiencyLevel !== undefined) {
        const preferencesValidation = validateUserPreferences(updateData.nativeLanguage, updateData.learningLanguages, updateData.proficiencyLevel);
        allErrors.push(...preferencesValidation.errors);
        allWarnings.push(...preferencesValidation.warnings);
    }
    return {
        isValid: allErrors.length === 0,
        errors: allErrors,
        warnings: allWarnings,
    };
}
//# sourceMappingURL=userValidation.js.map